import { useEffect, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2 } from "lucide-react";

// Two-level GL drill-down shared by Balance Sheet & P&L:
//   Level 1 — all GL entries of one account (from /ledger/entries)
//   Level 2 — the full double-entry of one voucher (/financial-statements/voucher-gl)
export const GLDrillDialog = ({ open, onClose, account }) => {
  const [entries, setEntries] = useState(null);
  const [voucher, setVoucher] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !account) return;
    setVoucher(null);
    setEntries(null);
    setLoading(true);
    api.get(`/ledger/entries?account_type=${encodeURIComponent(account.account_type)}&account_id=${encodeURIComponent(account.account_id)}`)
      .then(r => setEntries(r.data))
      .catch(() => setEntries({ entries: [] }))
      .finally(() => setLoading(false));
  }, [open, account]);

  const openVoucher = async (e) => {
    if (!e.ref_type || !e.ref_id) return;
    setLoading(true);
    try {
      const r = await api.get(`/financial-statements/voucher-gl?ref_type=${encodeURIComponent(e.ref_type)}&ref_id=${encodeURIComponent(e.ref_id)}`);
      setVoucher({ ...r.data, ref_no: e.ref_no || e.ref_id });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="gl-drill-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm">
            {voucher ? (
              <>
                <Button variant="ghost" size="sm" onClick={() => setVoucher(null)} data-testid="gl-drill-back-btn">
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back
                </Button>
                Voucher — {voucher.ref_type} · {voucher.ref_no}
              </>
            ) : (
              <>General Ledger — {account?.account_name}</>
            )}
          </DialogTitle>
        </DialogHeader>
        {loading && <div className="text-slate-500 text-sm p-4"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>}

        {!voucher && entries && (
          <div className="overflow-auto flex-1">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-slate-50 z-10">
                <tr className="text-left text-slate-500 border-b">
                  <th className="p-2">Date</th><th className="p-2">Voucher</th>
                  <th className="p-2">Narration</th>
                  <th className="p-2 text-right">Debit</th>
                  <th className="p-2 text-right">Credit</th>
                  <th className="p-2 text-right">Running</th>
                </tr>
              </thead>
              <tbody>
                {(entries.entries || []).map((e, i) => (
                  <tr key={i} className="border-b hover:bg-orange-50 cursor-pointer" onClick={() => openVoucher(e)} data-testid={`gl-drill-entry-${i}`}>
                    <td className="p-2 whitespace-nowrap">{(e.entry_date || "").slice(0, 10)}</td>
                    <td className="p-2 whitespace-nowrap text-orange-700">{e.ref_no || e.ref_type}</td>
                    <td className="p-2 max-w-[280px] truncate">{e.narration || e.description || ""}</td>
                    <td className="p-2 text-right tabular">{e.debit ? formatINR(e.debit) : ""}</td>
                    <td className="p-2 text-right tabular">{e.credit ? formatINR(e.credit) : ""}</td>
                    <td className="p-2 text-right tabular">{formatINR(e.running_balance)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="sticky bottom-0 bg-slate-100 font-bold">
                <tr>
                  <td className="p-2" colSpan={3}>Total</td>
                  <td className="p-2 text-right tabular">{formatINR(entries.total_debit)}</td>
                  <td className="p-2 text-right tabular">{formatINR(entries.total_credit)}</td>
                  <td className="p-2 text-right tabular">{formatINR(entries.closing_balance)}</td>
                </tr>
              </tfoot>
            </table>
            <div className="text-[10px] text-slate-400 p-2">Click any row to open the voucher's complete double-entry.</div>
          </div>
        )}

        {voucher && (
          <div className="overflow-auto flex-1">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-slate-50 z-10">
                <tr className="text-left text-slate-500 border-b">
                  <th className="p-2">Date</th><th className="p-2">Account</th>
                  <th className="p-2">Narration</th>
                  <th className="p-2 text-right">Debit</th>
                  <th className="p-2 text-right">Credit</th>
                </tr>
              </thead>
              <tbody>
                {(voucher.entries || []).map((e, i) => (
                  <tr key={i} className="border-b">
                    <td className="p-2 whitespace-nowrap">{(e.entry_date || "").slice(0, 10)}</td>
                    <td className="p-2">{e.account_name}</td>
                    <td className="p-2 max-w-[280px] truncate">{e.narration || e.description || ""}</td>
                    <td className="p-2 text-right tabular">{e.debit ? formatINR(e.debit) : ""}</td>
                    <td className="p-2 text-right tabular">{e.credit ? formatINR(e.credit) : ""}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="sticky bottom-0 bg-slate-100 font-bold">
                <tr>
                  <td className="p-2" colSpan={3}>Total (Dr must equal Cr)</td>
                  <td className="p-2 text-right tabular" data-testid="voucher-total-debit">{formatINR(voucher.total_debit)}</td>
                  <td className="p-2 text-right tabular" data-testid="voucher-total-credit">{formatINR(voucher.total_credit)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Shared reconciliation footer proving the statement reads only the verified GL.
export const ReconciliationFooter = ({ recon, testid }) => {
  if (!recon) return null;
  return (
    <div className="mt-6 border border-slate-200 bg-slate-50 p-4 text-xs" data-testid={testid}>
      <div className="font-bold text-slate-700 uppercase tracking-wider text-[10px] mb-2">Reconciliation — Single Source of Truth</div>
      <div className="text-slate-600 mb-2">{recon.source}</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div><span className="text-slate-400">Trial Balance Dr:</span> <span className="tabular font-semibold">{formatINR(recon.trial_balance_debit)}</span></div>
        <div><span className="text-slate-400">Trial Balance Cr:</span> <span className="tabular font-semibold">{formatINR(recon.trial_balance_credit)}</span></div>
        <div><span className="text-slate-400">Statement Dr:</span> <span className="tabular font-semibold">{formatINR(recon.statement_debit)}</span></div>
        <div><span className="text-slate-400">Statement Cr:</span> <span className="tabular font-semibold">{formatINR(recon.statement_credit)}</span></div>
      </div>
      <div className={`mt-2 font-bold ${recon.ties_to_trial_balance ? "text-emerald-700" : "text-red-700"}`} data-testid={`${testid}-tie`}>
        {recon.ties_to_trial_balance ? "✓ TIES TO TRIAL BALANCE EXACTLY" : "× DOES NOT TIE TO TRIAL BALANCE"}
      </div>
      {recon.accounts_unmapped > 0 && (
        <div className="text-amber-700 mt-1">
          {recon.accounts_unmapped} account(s) pending Ledger Master mapping (net {formatINR(recon.unmapped_amount)}) — shown in the UNMAPPED exception line, never guessed.
        </div>
      )}
    </div>
  );
};
